/**
 * Customer signing certs (G1) — admin roster + revoke.
 *
 * Layout:
 *   PageHeader → HeroPanel (active / rotated / revoked / expiring soon)
 *              → Status breakdown donut + Renewal calendar (side-by-side)
 *              → Toolbar (status chips)
 *              → DataTable with days-to-expiry chip
 *
 * Notes:
 *   - The plan called for an "issuer" donut (LE / commercial / self-signed)
 *     but the API doesn't expose issuer info — every customer cert is
 *     signed by the platform KMS. Status breakdown is the most actionable
 *     2D view we can ship today.
 *   - Customer certs have a 24h TTL; "expiring soon" is anything ≤ 6h.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  ShieldCheck, RefreshCw, Trash2, CalendarDays,
  PieChart as PieIcon,
} from 'lucide-react';
import {
  AdminApiClient,  type CustomerCertRow,
} from '../api';
import { userMessageFor, failureFrom, type Failure } from '../lib/api-state';
import { usePermissions } from '../lib/permissions';
import { AccessNotice } from '../components/ui/AccessNotice';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, type Band } from '../components/ui/StatusChip';
import { DataTable, type Column } from '../components/ui/DataTable';

type StatusFilter = 'active' | 'rotated' | 'revoked' | 'all';

const STATUS_TONES: Record<CustomerCertRow['status'], string> = {
  active:  '#34d399',
  rotated: '#94a3b8',
  revoked: '#f87171',
};

function bandForCert(status: CustomerCertRow['status']): Band {
  switch (status) {
    case 'active':  return 'healthy';
    case 'rotated': return 'neutral';
    case 'revoked': return 'critical';
  }
}

export function CustomerCertsPage(): JSX.Element {
  const { state } = useSession();
  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );
  const [rows, setRows] = useState<CustomerCertRow[] | null>(null);
  const [err, setErr] = useState<Failure | null>(null);
  const [version, setVersion] = useState(0);
  const perms = usePermissions();
  const [filter, setFilter] = useState<StatusFilter>('active');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    setRefreshing(true);
    api.customerCerts({})
      .then((r) => { setRows(r.certs); setErr(null); })
      .catch((e) => setErr(failureFrom(e, 'customer certificates')))
      .finally(() => setRefreshing(false));
  }, [api, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const onRevoke = async (kid: string): Promise<void> => {
    const reason = prompt(
      `Revoke cert ${kid.slice(0, 24)}…?\n\nThis takes effect immediately. The customer's signed traffic will fail until their next 6h refresh.\n\nEnter a short reason:`,
    );
    if (!reason || !reason.trim()) return;
    try {
      await api.revokeCustomerCert(kid, reason.trim());
      onRefresh();
    } catch (e) {
      alert(userMessageFor(e));
    }
  };

  const counts = useMemo(() => {
    let active = 0, rotated = 0, revoked = 0, expiringSoon = 0;
    const now = Date.now();
    const sixH = 6 * 3600 * 1000;
    for (const r of rows ?? []) {
      if (r.status === 'active')  active += 1;
      if (r.status === 'rotated') rotated += 1;
      if (r.status === 'revoked') revoked += 1;
      if (r.status === 'active') {
        const t = Date.parse(r.expires_at);
        if (Number.isFinite(t) && t > now && t < now + sixH) expiringSoon += 1;
      }
    }
    return { active, rotated, revoked, expiringSoon, total: (rows ?? []).length };
  }, [rows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return rows ?? [];
    return (rows ?? []).filter((r) => r.status === filter);
  }, [rows, filter]);

  const columns: Column<CustomerCertRow>[] = [
    {
      key: 'license',
      header: 'License',
      sortable: true,
      sortValue: (r) => r.license_id,
      cell: (r) => (
        <code
          style={{
            fontSize: '0.6875rem', color: 'var(--text-muted)',
            background: 'var(--bg-overlay)',
            padding: '2px 6px', borderRadius: 4,
            border: '1px solid var(--border)',
          }}
        >
          {r.license_id.slice(0, 8)}…
        </code>
      ),
    },
    {
      key: 'kid',
      header: 'Key id',
      cell: (r) => (
        <code style={{ fontSize: '0.75rem' }}>{r.kid.slice(0, 18)}…</code>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <StatusChip band={bandForCert(r.status)}>{r.status}</StatusChip>
      ),
    },
    {
      key: 'ttl',
      header: 'TTL',
      cell: (r) => <TtlChip cert={r} />,
    },
    {
      key: 'issued',
      header: 'Issued',
      sortable: true,
      sortValue: (r) => Date.parse(r.issued_at),
      align: 'right',
      cell: (r) => (
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {fmtRelative(r.issued_at)}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Revoke reason',
      cell: (r) => r.revoked_at ? (
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {r.revoked_reason ?? '(no reason)'}
        </span>
      ) : <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    ...(perms.canWrite ? [{
      key: 'actions',
      header: '',
      align: 'right' as const,
      cell: (r: CustomerCertRow) => r.status === 'active' ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => { e.stopPropagation(); void onRevoke(r.kid); }}
        >
          <Trash2 width={12} height={12} />
          Revoke
        </Button>
      ) : <></>,
    }] : []),
  ];

  const statusChips: Array<{ key: StatusFilter; label: string; count: number }> = [
    { key: 'active',  label: 'Active',  count: counts.active },
    { key: 'rotated', label: 'Rotated', count: counts.rotated },
    { key: 'revoked', label: 'Revoked', count: counts.revoked },
    { key: 'all',     label: 'All',     count: counts.total },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Customer signing certs"
        subtitle="Short-lived Ed25519 certs (24h TTL) issued by the upstream KMS to customer Vloud installs. Customers refresh every 6h; revocation is immediate."
        icon={ShieldCheck}
        accent="#34d399"
        actions={
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw width={14} height={14}
              style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
            Refresh
          </Button>
        }
      />

      <AccessNotice reason={perms.canWrite ? null : `${perms.writeReason ?? ''} Revoking certificates is not available to you.`} />

      {rows && (<>
      <HeroPanel
        icon={ShieldCheck}
        accent="#34d399"
        title="Cert health"
        subtitle="Live vs rotated vs revoked, plus the rotation cohort that's about to refresh."
        kpis={[
          <HeroKpi key="a" label="Active" value={counts.active} band={counts.active > 0 ? 'healthy' : 'neutral'} />,
          <HeroKpi key="r" label="Rotated" value={counts.rotated} band="neutral" hint="naturally retired" />,
          <HeroKpi key="x" label="Revoked" value={counts.revoked}
                   band={counts.revoked > 0 ? 'critical' : 'neutral'} />,
          <HeroKpi key="s" label="Expiring (6h)" value={counts.expiringSoon}
                   band={counts.expiringSoon > 0 ? 'warning' : 'healthy'}
                   hint="customers refresh @ 6h, so this is the about-to-rotate cohort" />,
        ]}
      />

      <div
        style={{
          marginTop: 16,
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
        }}
      >
        <StatusDonut counts={counts} />
        <RenewalCalendar rows={rows} />
      </div>
      </>)}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows && <ChipRow chips={statusChips} value={filter} onChange={setFilter} />}

        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(r) => r.id}
          loading={!rows && !err}
          error={err}
          onRetry={onRefresh}
          emptyTitle="No certs match"
          emptyDescription="Adjust filters or refresh — certs appear as customer engines refresh."
        />
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Status donut chart (inline SVG)
 * ────────────────────────────────────────────────────────────────── */

function StatusDonut({
  counts,
}: { counts: { active: number; rotated: number; revoked: number; total: number } }): JSX.Element {
  const slices = [
    { key: 'active',  label: 'Active',  count: counts.active,  tone: STATUS_TONES.active },
    { key: 'rotated', label: 'Rotated', count: counts.rotated, tone: STATUS_TONES.rotated },
    { key: 'revoked', label: 'Revoked', count: counts.revoked, tone: STATUS_TONES.revoked },
  ];
  const total = slices.reduce((s, o) => s + o.count, 0);

  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <PieIcon width={14} height={14} color="var(--text-muted)" />
        <span
          style={{
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Status breakdown
        </span>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <DonutSvg slices={slices.map((s) => ({ value: s.count, color: s.tone }))} total={total} />
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {slices.map((s) => {
            const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
            return (
              <li
                key={s.key}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: '0.75rem',
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 2, background: s.tone }} />
                <span style={{ flex: 1, color: 'var(--text-primary)' }}>{s.label}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
                  {s.count.toLocaleString()} ({pct}%)
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function DonutSvg({
  slices, total,
}: {
  slices: Array<{ value: number; color: string }>;
  total: number;
}): JSX.Element {
  const r = 32;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={88} height={88} viewBox="0 0 80 80" style={{ flex: 'none' }}>
      <circle cx={40} cy={40} r={r} fill="none" stroke="var(--bg-overlay)" strokeWidth={12} />
      {total > 0 && slices.map((s, i) => {
        if (s.value === 0) return null;
        const len = (s.value / total) * c;
        const dasharray = `${len} ${c - len}`;
        const dashoffset = -offset;
        offset += len;
        return (
          <circle
            key={i}
            cx={40} cy={40} r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={12}
            strokeDasharray={dasharray}
            strokeDashoffset={dashoffset}
            transform="rotate(-90 40 40)"
            strokeLinecap="butt"
          />
        );
      })}
      <text
        x={40} y={40}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="14"
        fontWeight="600"
        fill="var(--text-primary)"
      >
        {total}
      </text>
    </svg>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Renewal calendar (month-grid heatmap of upcoming expiries)
 * ────────────────────────────────────────────────────────────────── */

function RenewalCalendar({ rows }: { rows: CustomerCertRow[] }): JSX.Element {
  const cells = useMemo(() => {
    // Show next 24 hours in 1-hour buckets — that's the cert lifecycle.
    const now = new Date();
    const out: Array<{ key: string; label: string; count: number; bucket: number }> = [];
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getTime() + i * 3600 * 1000);
      const hour = d.getHours();
      out.push({
        key: d.toISOString().slice(0, 13),
        label: hour.toString().padStart(2, '0') + ':00',
        count: 0,
        bucket: i,
      });
    }
    const idx = new Map(out.map((c, i) => [c.bucket, i] as const));
    for (const r of rows) {
      if (r.status !== 'active') continue;
      const t = Date.parse(r.expires_at);
      if (!Number.isFinite(t)) continue;
      const diffH = Math.floor((t - now.getTime()) / 3600 / 1000);
      if (diffH < 0 || diffH >= 24) continue;
      const i = idx.get(diffH);
      if (i !== undefined && out[i]) out[i].count += 1;
    }
    return out;
  }, [rows]);

  const max = Math.max(1, ...cells.map((c) => c.count));

  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <CalendarDays width={14} height={14} color="var(--text-muted)" />
        <span
          style={{
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Renewal calendar · next 24h
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '0.6875rem',
            color: 'var(--text-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {cells.reduce((s, c) => s + c.count, 0)} expiring
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
          gap: 4,
        }}
      >
        {cells.map((c) => {
          const ratio = c.count === 0 ? 0 : 0.2 + 0.8 * (c.count / max);
          const bg = c.count === 0
            ? 'rgba(148,163,184,0.06)'
            : `hsla(152 80% 50% / ${ratio})`;
          return (
            <div
              key={c.key}
              title={`${c.label} · ${c.count} expiring`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 36,
                borderRadius: 6,
                background: bg,
                border: '1px solid rgba(148,163,184,0.10)',
                color: ratio > 0.5 ? '#0b0f19' : 'var(--text-muted)',
              }}
            >
              <span style={{ fontSize: '0.625rem', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                +{c.bucket}h
              </span>
              <span style={{ fontSize: '0.6875rem', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {c.count}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * TTL chip
 * ────────────────────────────────────────────────────────────────── */

function TtlChip({ cert }: { cert: CustomerCertRow }): JSX.Element {
  if (cert.status !== 'active') {
    return <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>—</span>;
  }
  const t = Date.parse(cert.expires_at);
  const now = Date.now();
  const diffH = (t - now) / 3600 / 1000;
  let band: Band = 'healthy';
  let label = '';
  if (diffH < 0) {
    band = 'critical';
    label = 'expired';
  } else if (diffH < 1) {
    band = 'critical';
    label = `${Math.round(diffH * 60)}m`;
  } else if (diffH < 6) {
    band = 'warning';
    label = `${Math.round(diffH)}h`;
  } else {
    band = 'healthy';
    label = `${Math.round(diffH)}h`;
  }
  return <StatusChip band={band} size="sm" pulse={diffH < 1}>{label}</StatusChip>;
}

/* ──────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────── */

function ChipRow<T extends string>({
  chips, value, onChange,
}: {
  chips: Array<{ key: T; label: string; count: number }>;
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Filters"
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-overlay)',
        flexWrap: 'wrap', width: 'fit-content',
      }}
    >
      {chips.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            aria-pressed={active}
            onClick={() => onChange(c.key)}
            style={{
              display: 'inline-flex', gap: 5, alignItems: 'center',
              padding: '5px 9px', border: 'none', borderRadius: 8,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
            }}
          >
            {c.label}
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.6875rem',
                color: 'var(--text-muted)',
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
                padding: '0 6px',
                borderRadius: 999,
                minWidth: 18, textAlign: 'center',
              }}
            >{c.count}</span>
          </button>
        );
      })}
    </div>
  );
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

